import { InputAdornment } from "@deere/fuel-react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import {
  FormControl,
  FormControlProps,
  IconButton,
  InputLabel,
  TextField,
  TextFieldProps,
} from "@mui/material";
import Popover from "@mui/material/Popover";
import { useTheme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PickerValue } from "@mui/x-date-pickers/internals";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar, DateCalendarProps } from "@mui/x-date-pickers-pro";
import dayjs, { Dayjs } from "dayjs";
import { MouseEventHandler, useEffect, useState } from "react";
import {
  getFirstEnabledMonth,
  isDateValid,
} from "../../../pages/orders/components/OrderHeader/components/RequestedShipMonthSelector/utils";

export interface DatePickerProps {
  label?: string | null;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  onChange: (selection: Dayjs | undefined) => void;
  value?: Dayjs | null | undefined;
  format?: string;
  renderValue?: (selection: Dayjs | null) => string;
  textFieldProps?: TextFieldProps;
  dateCalendarProps?: DateCalendarProps;
  formControlProps?: FormControlProps;
  hideLabel?: boolean;
  handleOnClick?: () => void;
}

const renderDates =
  (format: string) =>
  (selection: Dayjs | null): string => {
    if (!selection) return "";
    return selection?.format(format);
  };

const DatePicker = ({
  label = "Select Date",
  placeholder = "Select a Date",
  required,
  error,
  onChange,
  value: initialValue = null,
  helperText = "",
  format = "DD-MMM-YYYY",
  renderValue = renderDates(format),
  textFieldProps = {},
  dateCalendarProps = {},
  formControlProps = {},
  hideLabel = false,
  handleOnClick = () => {},
}: DatePickerProps) => {
  const theme = useTheme();

  const [value, setValue] = useState<Dayjs | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    // @ts-ignore
    setAnchorEl(event.currentTarget);
    handleOnClick();
  };
  const handleClose = () => setAnchorEl(null);

  useEffect(() => {
    setValue(initialValue ?? null);
  }, [initialValue]);

  const open = Boolean(anchorEl);
  const id = open ? "date-range-popover" : undefined;

  const handleDatePickerChange = (val: PickerValue) => {
    let newValue = dayjs(val);

    const isMonthValid = newValue
      ? !dateCalendarProps.shouldDisableMonth?.(newValue)
      : false;
    const { shouldDisableMonth, shouldDisableYear } = dateCalendarProps;
    if (newValue && value?.year() !== newValue.year()) {
      if (!isMonthValid) {
        const firstEnabledMonth = getFirstEnabledMonth(
          newValue.year(),
          newValue,
          shouldDisableMonth
        );
        if (firstEnabledMonth !== null) {
          newValue = newValue.set("month", firstEnabledMonth);
        }
      }
    }

    if (isDateValid(newValue, shouldDisableMonth, shouldDisableYear)) {
      setValue(newValue);
      onChange(newValue);
    }
    handleClose();
  };

  return (
    <FormControl data-testid="date-picker" {...formControlProps}>
      {!hideLabel && label && (
        <InputLabel data-testid="date-picker-label" required={required}>
          {label}
        </InputLabel>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TextField
          aria-describedby={id}
          onClick={handleClick}
          placeholder={placeholder}
          data-testid="date-picker-text-field"
          error={error}
          InputProps={{
            sx: {
              ...(error && {
                borderColor: theme.tokens.FuelColorAlertErrorBorder,
              }),
              padding: "0 !important",
              alignItems: "center !important",
              ".MuiInputBase-input": {
                textOverflow: "unset",
                fontSize: theme.tokens.FuelFontSizeMd,
              },
            },
            startAdornment: (
              <CalendarMonthIcon
                sx={{
                  marginLeft: theme.tokens.FuelSpacing1,
                  color: theme.tokens.FuelColorGray700,
                }}
              />
            ),
            endAdornment: value && (
              <InputAdornment position="end" sx={{ padding: 0 }}>
                <IconButton
                  size="small"
                  sx={{
                    padding: 0,
                  }}
                  onClick={(e) => {
                    setValue(null);
                    onChange(undefined);
                    e.stopPropagation();
                  }}
                >
                  <CloseIcon sx={{ padding: 0 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          helperText={helperText}
          value={renderValue(value as Dayjs | null)}
          sx={{
            width: "fit-content",
            ".MuiInputBase-root": {
              padding: "0 !important",
              alignItems: "center !important",
            },
          }}
          {...textFieldProps}
        />
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <DateCalendar
            data-testid="date-picker-calendar"
            value={value}
            onChange={handleDatePickerChange}
            {...dateCalendarProps}
          />
        </Popover>
      </LocalizationProvider>
    </FormControl>
  );
};

export default DatePicker;
